/**
 * Flip gizmos for a selected door/window OPENING_SKETCH — extracted from the
 * OpeningSketchDrawTool so the Design tab's selection tool shows them too.
 *
 * Two double-arrow handles sit off the door midpoint:
 *  - Side: across the wall (along the normal) — flips which face the
 *    swing/inset is on. Always present.
 *  - Hinge: along the wall (between the jambs) — flips the hinge end. Only
 *    for doors that aren't open doorways (hinge has no meaning otherwise).
 *
 * The owning tool drives this per frame (`show`/`hide`), picks with
 * `pickAtPixel`, and flips with `flip` (undo snapshot included).
 */

import * as THREE from 'three';
import { Viewer } from '../../../../viewer/viewer';
import { RoundHandleMarker } from '../../../../utils/RoundHandleMarker';
import { Utils } from '../../../../utils';
import { pointAtArcLength } from '../../../../utils/skeleton/arcGeometry';
import { PointoramaWallSketch } from '../../../../utils/PointoramaWallSketch';
import { PixelHitTester } from '../selection/PixelHitTester';
import {
  openingFootprint,
  clampOpeningOffsetToClearSpan,
  cursorOffsetOnOpeningEdge,
  cursorPerpOnOpeningEdge,
  createOpeningOffsetMoveSnapshot,
  createOpeningFloatToggleSnapshot,
  WallHoleRebuildScheduler,
  OpeningFootprintHighlight,
} from '../openingSketch/OpeningSketchShared';
import { Snapshot, ToolUndoRedoManager } from '../../ToolUndoRedoManager';
import { OpeningKind, DoorType, type DoorGlyphType } from 'shared-types';
import {
  getOpeningSketchOffsetProperty,
  getOpeningSketchLengthProperty,
  getOpeningSketchWallEdgeIdProperty,
  getOpeningSketchInsetProperty,
  getOpeningSketchKindProperty,
  getOpeningSketchDoorTypeProperty,
  getOpeningSketchHingeAtStartProperty,
  getOpeningSketchSwingPositiveProperty,
} from '@pointorama/potree-shared';
import type { ReactiveProperty } from '@pointorama/potree-shared';

/** Pixel radius for picking one of the flip gizmos (Side / Hinge). */
export const HANDLE_PICK_RADIUS_PX = 26;
/** Screen-space separation (px) of the two flip gizmos from each other along
 *  the wall, so they don't overlap. */
const HANDLE_SEPARATION_PX = 42;
/** Screen-space distance (px) both flip gizmos sit OFF the wall (along the
 *  normal), clear of the door glyph so they don't clutter the opening line. */
const HANDLE_OFFWALL_PX = 60;
/** World caps (as a fraction of the door span) applied when zoomed far out so
 *  the gizmos + their offsets stay fit to the door instead of dwarfing it.
 *  Max-only — when zoomed in the screen-space sizes/offsets apply unchanged. */
const HANDLE_MAX_ARROW_DOOR_FRAC = 0.4;
const HANDLE_MAX_OFFWALL_DOOR_FRAC = 0.45;
const HANDLE_MAX_SEP_DOOR_FRAC = 0.25;

export type FlipHandleKind = 'side' | 'hinge';

interface FlipGizmoDeps {
  viewer: Viewer;
  hitTester: PixelHitTester;
  /** Overlay group the arrow markers are parented to (owned by the tool). */
  ensureOverlay: () => THREE.Group;
  undoRedo: () => ToolUndoRedoManager | null;
  renderOrder: number;
}

export class OpeningFlipGizmos {
  private sideHandle: RoundHandleMarker | null = null;
  private hingeHandle: RoundHandleMarker | null = null;
  private sideHandlePos: THREE.Vector3 | null = null;
  private hingeHandlePos: THREE.Vector3 | null = null;
  private openingId: string | null = null;

  constructor(private deps: FlipGizmoDeps) {}

  /** The opening the gizmos currently belong to (null while hidden). */
  get currentOpeningId(): string | null {
    return this.openingId;
  }

  /**
   * Geometry for the two flip gizmos of an opening: door midpoint, local wall
   * tangent/normal (curved walls follow the curve) and hinge availability.
   */
  private computeHandlePositions(skeleton: PointoramaWallSketch, openingId: string): { center: THREE.Vector3; wallDir: THREE.Vector3; wallNorm: THREE.Vector3; hasHinge: boolean; swingPositive: boolean } | null {
    let edgeId = '';
    let offset = 0;
    let length = 0;
    let kind = OpeningKind.DOOR;
    let doorType: DoorGlyphType = DoorType.SINGLE;
    let swingPositive = true;
    try {
      edgeId = getOpeningSketchWallEdgeIdProperty(openingId).value || '';
      offset = getOpeningSketchOffsetProperty(openingId).value ?? 0;
      length = getOpeningSketchLengthProperty(openingId).value ?? 0;
      kind = getOpeningSketchKindProperty(openingId).value;
      doorType = getOpeningSketchDoorTypeProperty(openingId).value;
      swingPositive = getOpeningSketchSwingPositiveProperty(openingId).value;
    } catch {
      return null;
    }
    if (!edgeId) return null;
    const edge = skeleton.getEdge(edgeId);
    if (!edge) return null;
    const na = skeleton.getNode(edge.startNodeId);
    const nb = skeleton.getNode(edge.endNodeId);
    if (!na || !nb) return null;
    const aXY = { x: na.position.x, y: na.position.y };
    const bXY = { x: nb.position.x, y: nb.position.y };
    if (Math.hypot(bXY.x - aXY.x, bXY.y - aXY.y) < 1e-6) return null;
    const mid = pointAtArcLength(aXY, bXY, edge.bulge, offset + length / 2);
    const wallDir = new THREE.Vector3(mid.tangent.x, mid.tangent.y, 0);
    // Wall normal (90° CCW of the wall direction), matching frameFromWallOpeningSketch.
    const wallNorm = new THREE.Vector3(-mid.tangent.y, mid.tangent.x, 0);
    // Lift slightly above the floor so the gizmo reads on top of the glyph.
    const center = new THREE.Vector3(mid.point.x, mid.point.y, skeleton.getFloorHeight() + 0.06);
    const hasHinge = kind === OpeningKind.DOOR && doorType !== DoorType.OPEN_DOORWAY;
    return { center, wallDir, wallNorm, hasHinge, swingPositive };
  }

  /** Create/position the flip gizmos for `openingId`. */
  show(skeleton: PointoramaWallSketch, openingId: string): void {
    const geo = this.computeHandlePositions(skeleton, openingId);
    if (!geo) {
      this.hide();
      return;
    }
    const group = this.deps.ensureOverlay();
    this.openingId = openingId;

    // Door span (world) — caps the gizmo size + offsets so they stay FIT to
    // the door when zoomed far out. Max-only; zoomed in, the constant
    // screen-space sizes apply unchanged.
    const footprint = openingFootprint(skeleton, openingId);
    const doorLen = footprint ? Math.hypot(footprint.b.x - footprint.a.x, footprint.b.y - footprint.a.y) : 0;
    const maxArrowWorld = doorLen > 0 ? doorLen * HANDLE_MAX_ARROW_DOOR_FRAC : Infinity;
    const maxOffWallWorld = doorLen > 0 ? doorLen * HANDLE_MAX_OFFWALL_DOOR_FRAC : Infinity;
    const maxSepWorld = doorLen > 0 ? doorLen * HANDLE_MAX_SEP_DOOR_FRAC : Infinity;

    // Both gizmos push OFF the wall toward the side OPPOSITE the door swing,
    // clear of the glyph, and separate from each other along the wall.
    const offWallSign = geo.swingPositive ? -1 : 1;
    const offWallDist = Math.min(this.pixelsToWorldAt(geo.center, HANDLE_OFFWALL_PX), maxOffWallWorld);
    const offWall = geo.center.clone().addScaledVector(geo.wallNorm, offWallSign * offWallDist);
    const sepWorld = geo.hasHinge ? Math.min(this.pixelsToWorldAt(geo.center, HANDLE_SEPARATION_PX), maxSepWorld) : 0;
    const sidePos = offWall.clone().addScaledVector(geo.wallDir, -sepWorld);
    const hingePos = offWall.clone().addScaledVector(geo.wallDir, +sepWorld);

    // Side flip: double-arrow ACROSS the wall (along the normal).
    if (!this.sideHandle) {
      this.sideHandle = new RoundHandleMarker(this.deps.viewer);
      this.sideHandle.setRenderOrder(this.deps.renderOrder);
      group.add(this.sideHandle);
    }
    this.sideHandle.setMaxWorldLength(maxArrowWorld);
    this.sideHandle.position.copy(sidePos);
    this.sideHandle.setDirection(geo.wallNorm);
    this.sideHandle.visible = true;
    // Size NOW so the first rendered frame isn't at a stale/huge scale.
    this.sideHandle.updateScale();
    this.sideHandlePos = sidePos.clone();

    // Hinge flip: double-arrow ALONG the wall (between jambs).
    if (geo.hasHinge) {
      if (!this.hingeHandle) {
        this.hingeHandle = new RoundHandleMarker(this.deps.viewer);
        this.hingeHandle.setRenderOrder(this.deps.renderOrder);
        group.add(this.hingeHandle);
      }
      this.hingeHandle.setMaxWorldLength(maxArrowWorld);
      this.hingeHandle.position.copy(hingePos);
      this.hingeHandle.setDirection(geo.wallDir);
      this.hingeHandle.visible = true;
      // Size NOW so the first rendered frame isn't at a stale/huge scale.
      this.hingeHandle.updateScale();
      this.hingeHandlePos = hingePos.clone();
    } else if (this.hingeHandle) {
      this.hingeHandle.visible = false;
      this.hingeHandlePos = null;
    }
  }

  /** Convert a screen-space pixel distance to world units at a world point,
   *  using the active camera (so gizmo separation stays constant on zoom). */
  private pixelsToWorldAt(worldPoint: THREE.Vector3, pixels: number): number {
    const camera = this.deps.viewer.scene.getActiveCamera();
    const size = this.deps.viewer.renderer.getSize(new THREE.Vector2());
    const distance = camera.position.distanceTo(worldPoint);
    const pr = Utils.projectedRadius(1, camera, distance, size.width, size.height);
    return pr > 1e-6 ? pixels / pr : 0;
  }

  hide(): void {
    if (this.sideHandle) this.sideHandle.visible = false;
    if (this.hingeHandle) this.hingeHandle.visible = false;
    this.sideHandlePos = null;
    this.hingeHandlePos = null;
    this.openingId = null;
  }

  /**
   * Which flip gizmo (if any) is under the cursor. The two arrows sit at
   * separate points (offset along the wall), so pick whichever center is
   * closest within the pick radius.
   */
  pickAtPixel(mousePx: THREE.Vector2): FlipHandleKind | null {
    let best: FlipHandleKind | null = null;
    let bestDist = HANDLE_PICK_RADIUS_PX;
    if (this.sideHandlePos) {
      const px = this.deps.hitTester.projectWorldToPixel(this.sideHandlePos);
      if (px) {
        const d = px.distanceTo(mousePx);
        if (d < bestDist) {
          bestDist = d;
          best = 'side';
        }
      }
    }
    if (this.hingeHandlePos) {
      const px = this.deps.hitTester.projectWorldToPixel(this.hingeHandlePos);
      if (px) {
        const d = px.distanceTo(mousePx);
        if (d < bestDist) {
          bestDist = d;
          best = 'hinge';
        }
      }
    }
    return best;
  }

  /**
   * Flip Side (swing/inset face → `swing_positive`; inset is stored relative
   * to the outer face on the current side, so it's preserved for free) or the
   * hinge end (`hinge_at_start`). The glyph rebuilds via the property
   * subscription. Pushes a toggle undo snapshot.
   */
  flip(which: FlipHandleKind, openingId: string): void {
    const getter = which === 'side' ? getOpeningSketchSwingPositiveProperty : getOpeningSketchHingeAtStartProperty;
    try {
      const prop = getter(openingId);
      const before = prop.value;
      prop.value = !before;
      this.deps.undoRedo()?.pushState(this.createBoolToggleSnapshot(getter, openingId, before, prop.value));
      // A Side flip changes which room the opening belongs to (a door files
      // into the room it swings INTO) — re-file it like a move does (handled
      // in usePotreeEvents). Hinge flips don't change the side.
      if (which === 'side') {
        this.deps.viewer.scene.dispatchEvent({ type: 'opening_sketch_moved', value: { identifier: openingId } });
      }
    } catch {
      /* property service not ready */
    }
  }

  /** Undo snapshot for a boolean toggle property: flips between before/after. */
  private createBoolToggleSnapshot(getter: (id: string) => ReactiveProperty<boolean>, openingId: string, before: boolean, after: boolean): Snapshot {
    let applyBefore = true;
    const snapshot: Snapshot = {
      restore: (): Snapshot => {
        try {
          getter(openingId).value = applyBefore ? before : after;
        } catch {
          /* property service not ready */
        }
        applyBefore = !applyBefore;
        return snapshot;
      },
    };
    return snapshot;
  }

  /** Drop the marker objects (the overlay group they lived in is being torn
   *  down); safe to keep using the controller afterwards. */
  dispose(): void {
    this.sideHandle?.dispose();
    this.sideHandle = null;
    this.hingeHandle?.dispose();
    this.hingeHandle = null;
    this.sideHandlePos = null;
    this.hingeHandlePos = null;
    this.openingId = null;
  }
}

interface FlipGizmoInteractionDeps {
  viewer: Viewer;
  hitTester: PixelHitTester;
  undoRedo: () => ToolUndoRedoManager | null;
  getWallSketch: () => PointoramaWallSketch | null;
  /** Extra visibility gate — return false to hide (e.g. while a nested
   *  sub-editor owns the viewport). Defaults to always-on. */
  shouldShow?: () => boolean;
  /**
   * Manage `inputHandler.selectionDisabled` around gizmo clicks: the gizmos
   * sit over empty space, so without suppression the click's normal pick
   * would deselect the opening before the flip. Tools that keep selection
   * suppressed for their whole lifetime (DoorWindowTool) pass false.
   */
  manageSelectionSuppression: boolean;
}

/** Press-time grab data for dragging an opening via a gizmo handle. */
interface HandleDragGrab {
  edgeId: string;
  beforeOffset: number;
  /** Cursor's along-wall offset minus `beforeOffset` at grab time. */
  grabDelta: number;
  beforeInset: number;
  /** Cursor's signed perpendicular off the centerline at grab time. */
  grabPerp: number;
  /** Side the inset is measured from at grab time (perp→inset sign). */
  swingPositive: boolean;
}

/**
 * Drop-in flip-gizmo hosting for selection-capable tools: owns the overlay
 * group and the per-frame "single selected opening → show gizmos" refresh,
 * and packages the mouse choreography. A plain click on a handle FLIPS; a
 * real drag MOVES the opening along the handle's axis — the Hinge gizmo
 * (along the wall) slides the `offset`, the Side gizmo (across the wall)
 * shifts the `inset` — mirroring the layout draw tool's handle-drag UX.
 * Call the handle* methods first in the tool's handlers — a `true` return
 * means the gizmos consumed the interaction (forward onDrag/onDragEnd to
 * handleDrag/handleDragEnd while a press is armed).
 */
export class OpeningFlipGizmoInteraction {
  private overlay: THREE.Group | null = null;
  private gizmos: OpeningFlipGizmos;
  /** Mousedown-armed interaction on a gizmo handle. `drag` is null when the
   *  press could not resolve the edge/cursor (property service not ready) —
   *  the press then stays flip-only. */
  private armed: { openingId: string; handle: FlipHandleKind; drag: HandleDragGrab | null; moved: boolean } | null = null;
  /** Whether we blocked the orbit controls for the current press. */
  private orbitBlocked = false;
  private holeRebuild: WallHoleRebuildScheduler;
  private highlight: OpeningFootprintHighlight;

  constructor(private deps: FlipGizmoInteractionDeps) {
    this.gizmos = new OpeningFlipGizmos({
      viewer: deps.viewer,
      hitTester: deps.hitTester,
      ensureOverlay: () => this.ensureOverlay(),
      undoRedo: deps.undoRedo,
      renderOrder: 999,
    });
    this.holeRebuild = new WallHoleRebuildScheduler(() => this.deps.getWallSketch());
    this.highlight = new OpeningFootprintHighlight(deps.viewer, () => this.ensureOverlay());
  }

  private ensureOverlay(): THREE.Group {
    if (!this.overlay) {
      this.overlay = new THREE.Group();
      this.overlay.name = 'opening_flip_gizmos_overlay';
      this.deps.viewer.uiScene.add(this.overlay);
    }
    return this.overlay;
  }

  /** Start the per-frame refresh (call from the tool's onEnable). */
  attach(): void {
    this.deps.viewer.addEventListener('update', this.refresh);
  }

  /** Tear everything down (call from the tool's onDisable). */
  detach(): void {
    this.deps.viewer.removeEventListener('update', this.refresh);
    if (this.armed && this.deps.manageSelectionSuppression) {
      this.deps.viewer.inputHandler.selectionDisabled = false;
    }
    this.armed = null;
    this.releaseOrbitBlock();
    this.holeRebuild.cancel();
    this.highlight.dispose();
    this.gizmos.dispose();
    if (this.overlay) {
      this.deps.viewer.uiScene.remove(this.overlay);
      this.overlay = null;
    }
  }

  /** Per-frame: a SINGLE selected opening on the active floor shows the
   *  gizmos; none/multi (or a false shouldShow gate) hides them. */
  private refresh = (): void => {
    const skeleton = this.deps.getWallSketch();
    if (!skeleton || !(this.deps.shouldShow?.() ?? true)) {
      this.gizmos.hide();
      return;
    }
    const selected = this.deps.viewer.scene.openingSketches.filter((os) => os.groupIdentifier === skeleton.groupIdentifier && os.selected);
    if (selected.length === 1) this.gizmos.show(skeleton, selected[0].identifier);
    else this.gizmos.hide();
  };

  /** True when the cursor is over a visible gizmo (show a pointer cursor). */
  isOverHandle(event: MouseEvent): boolean {
    return this.gizmos.currentOpeningId != null && this.gizmos.pickAtPixel(this.deps.hitTester.mousePx(event)) !== null;
  }

  /** Arm a flip/drag when the mousedown hits a gizmo. True = consumed. */
  handleMouseDown(event: MouseEvent): boolean {
    if (event.button !== 0) return false;
    const openingId = this.gizmos.currentOpeningId;
    const handle = openingId ? this.gizmos.pickAtPixel(this.deps.hitTester.mousePx(event)) : null;
    if (!handle || !openingId) {
      this.armed = null;
      return false;
    }
    this.armed = { openingId, handle, drag: this.captureDragGrab(event, openingId), moved: false };
    if (this.deps.manageSelectionSuppression) {
      this.deps.viewer.inputHandler.selectionDisabled = true;
    }
    // Block orbit right now rather than at the drag threshold: waiting loses
    // the race against the camera controls, which pan during the first pixels
    // (same reasoning as the draw tool's press-time block). Released in
    // handleClick / handleDragEnd / detach.
    this.deps.viewer.inputHandler.blockOrbitControls();
    this.orbitBlocked = true;
    return true;
  }

  /** Resolve the press-time grab data for a handle drag. Null (flip-only
   *  press) when the opening's edge or the cursor projection is unavailable. */
  private captureDragGrab(event: MouseEvent, openingId: string): HandleDragGrab | null {
    const skeleton = this.deps.getWallSketch();
    if (!skeleton) return null;
    try {
      const edgeId = getOpeningSketchWallEdgeIdProperty(openingId).value || '';
      if (!edgeId) return null;
      const cursorAlong = cursorOffsetOnOpeningEdge(event, this.deps.viewer, skeleton, edgeId);
      const cursorPerp = cursorPerpOnOpeningEdge(event, this.deps.viewer, skeleton, edgeId);
      if (cursorAlong === null || cursorPerp === null) return null;
      const beforeOffset = getOpeningSketchOffsetProperty(openingId).value ?? 0;
      return {
        edgeId,
        beforeOffset,
        grabDelta: cursorAlong - beforeOffset,
        beforeInset: getOpeningSketchInsetProperty(openingId).value ?? 0,
        grabPerp: cursorPerp,
        swingPositive: getOpeningSketchSwingPositiveProperty(openingId).value,
      };
    } catch {
      /* property service not ready */
      return null;
    }
  }

  /** The drag threshold was crossed. True = a gizmo press owns this drag —
   *  keep forwarding the tool's onDrag/onDragEnd to handleDrag/handleDragEnd. */
  handleDragStart(): boolean {
    return this.armed !== null;
  }

  /** Dragging a gizmo handle MOVES the opening along the handle's axis:
   *  Hinge (along the wall) slides the offset, Side (across the wall) shifts
   *  the inset. True = consumed. */
  handleDrag(event: MouseEvent): boolean {
    const armed = this.armed;
    if (!armed) return false;
    const drag = armed.drag;
    const skeleton = this.deps.getWallSketch();
    // The press owns the gesture even when it can't move (flip-only arm).
    if (!drag || !skeleton) return true;
    if (armed.handle === 'hinge') {
      const cursor = cursorOffsetOnOpeningEdge(event, this.deps.viewer, skeleton, drag.edgeId);
      if (cursor === null) return true;
      let length = 0;
      try {
        length = getOpeningSketchLengthProperty(armed.openingId).value ?? 0;
      } catch {
        return true;
      }
      const clamped = clampOpeningOffsetToClearSpan(skeleton, drag.edgeId, length, cursor - drag.grabDelta);
      try {
        getOpeningSketchOffsetProperty(armed.openingId).value = clamped;
      } catch {
        return true;
      }
      // Follow the slide: footprint highlight + live hole rebuild.
      const footprint = openingFootprint(skeleton, armed.openingId);
      if (footprint) this.highlight.show([footprint.pts]);
      this.holeRebuild.schedule();
    } else {
      const perp = cursorPerpOnOpeningEdge(event, this.deps.viewer, skeleton, drag.edgeId);
      if (perp === null) return true;
      // Inset grows INWARD from the outer face on the swing side, so a perp
      // move maps to inset with the side's sign; clamped to the wall
      // thickness (glyph stays between the two faces).
      const deltaPerp = perp - drag.grabPerp;
      const rawInset = drag.beforeInset + (drag.swingPositive ? -deltaPerp : deltaPerp);
      const edge = skeleton.getEdge(drag.edgeId);
      const thickness = (edge?.leftSize ?? 0.1) + (edge?.rightSize ?? 0.1);
      try {
        getOpeningSketchInsetProperty(armed.openingId).value = Math.max(0, Math.min(thickness, rawInset));
      } catch {
        return true;
      }
    }
    armed.moved = true;
    return true;
  }

  /** Finish a handle drag: commit the moved offset/inset with an undo
   *  snapshot and re-cut the wall hole at the settled position. The armed
   *  state lingers until the trailing click is swallowed in handleClick,
   *  then cleared as a fallback. True = consumed. */
  handleDragEnd(): boolean {
    const armed = this.armed;
    if (!armed) return false;
    if (this.deps.manageSelectionSuppression) {
      this.deps.viewer.inputHandler.selectionDisabled = false;
    }
    this.releaseOrbitBlock();
    this.highlight.hide();
    // A real drag consumed the interaction even if nothing moved (e.g. the
    // cursor projection failed) — the trailing click must NOT flip.
    const moved = armed.moved;
    armed.moved = true;
    setTimeout(() => {
      if (this.armed === armed) this.armed = null;
    }, 0);
    const drag = armed.drag;
    if (!moved || !drag) return true;
    if (armed.handle === 'hinge') {
      this.holeRebuild.cancel();
      let afterOffset = drag.beforeOffset;
      try {
        afterOffset = getOpeningSketchOffsetProperty(armed.openingId).value ?? drag.beforeOffset;
      } catch {
        /* property service not ready */
      }
      if (Math.abs(afterOffset - drag.beforeOffset) > 1e-6) {
        this.deps.undoRedo()?.pushState(createOpeningOffsetMoveSnapshot(this.deps.getWallSketch, armed.openingId, drag.beforeOffset, afterOffset));
        // Final authoritative hole re-cut at the settled position.
        this.deps.getWallSketch()?.updateSkinningVisualization();
      }
    } else {
      let afterInset = drag.beforeInset;
      try {
        afterInset = getOpeningSketchInsetProperty(armed.openingId).value ?? drag.beforeInset;
      } catch {
        /* property service not ready */
      }
      if (Math.abs(afterInset - drag.beforeInset) > 1e-6) {
        this.deps.undoRedo()?.pushState(createOpeningFloatToggleSnapshot(getOpeningSketchInsetProperty, armed.openingId, drag.beforeInset, afterInset));
      }
    }
    return true;
  }

  /** Perform the armed flip (or swallow the trailing click of a drag-move).
   *  True = consumed (the tool's own click logic must not run). */
  handleClick(): boolean {
    const armed = this.armed;
    if (!armed) return false;
    this.armed = null;
    if (this.deps.manageSelectionSuppression) {
      this.deps.viewer.inputHandler.selectionDisabled = false;
    }
    this.releaseOrbitBlock();
    if (armed.moved) return true;
    const openingId = this.gizmos.currentOpeningId;
    const skeleton = this.deps.getWallSketch();
    if (openingId && skeleton) {
      this.gizmos.flip(armed.handle, openingId);
      // Reposition the handles to follow the flipped state immediately.
      this.gizmos.show(skeleton, openingId);
    }
    return true;
  }

  private releaseOrbitBlock(): void {
    if (!this.orbitBlocked) return;
    this.orbitBlocked = false;
    this.deps.viewer.inputHandler.unblockOrbitControls();
  }
}
